import { createDarkTheme, type BrandVariants, type Theme } from "@fluentui/react-components";

/**
 * Paebbl brand theme for the add-in.
 *
 * Colors are the company's official design tokens (mirrored from
 * `styles/paebbl/global/palette.css`, which is auto-generated from the brand's
 * design system). The add-in is rendered as a full carbon-black surface — the
 * same dark, editorial look as paebbl.com — with ember as the primary accent.
 *
 * Two layers consume these values:
 *   1. `brandTheme` — a Fluent v9 dark theme so every Fluent control (Button,
 *      Input, Tab, Combobox, Checkbox, …) is brand-colored automatically.
 *   2. `ui` — semantic tokens for the inline-styled `div`s the components build
 *      by hand (rows, banners, labels), which Fluent's theme can't reach.
 */

/** Raw brand palette (hex), straight from the paebbl design tokens. */
export const palette = {
  carbonBlack: "#101820",
  white: "#ffffff",
  paleGraphite: "#98a2a0",
  platinum: "#dce6e4",
  // secondary (warm)
  copperRust: "#faaf3b",
  ember: "#d8692d",
  terracottaClay: "#b45236",
  rustwoodRed: "#973b26",
  // tertiary (cool)
  glacialBlue: "#9fd3d5",
  mineralTeal: "#68b1b3",
  oceanSlate: "#1e8387",
  basalt: "#124f51",
} as const;

/**
 * Semantic tokens for the hand-built (non-Fluent) UI, tuned to sit on a
 * carbon-black surface. Status backgrounds are deep, desaturated tints of the
 * relevant brand family with a light foreground for legibility on dark.
 */
export const ui = {
  /** Page background. */
  surface: palette.carbonBlack,
  /** Slightly raised surface for cards, inputs and dialogs. */
  surfaceRaised: "#18222c",
  /** A header bar a touch darker than the page for contrast. */
  surfaceHeader: "#0c1217",
  /** Background of a selected row (ember-tinted dark). */
  surfaceSelected: "#241a13",

  /** Hairline borders. */
  border: "#2c3a45",
  /** Even subtler divider (dashed separators, sub-headers). */
  borderSubtle: "#22303a",

  /** Primary text. */
  text: palette.white,
  /** Secondary text (platinum). */
  textSecondary: palette.platinum,
  /** Muted/metadata text (pale graphite). */
  textMuted: palette.paleGraphite,

  /** Primary accent (ember) + its on-color — used for main CTAs. */
  accent: palette.ember,
  accentText: palette.white,

  /** Warm highlight (copper rust) — wordmark, icons, selected-tab text. Sits on
   *  the dark surface, so it carries dark text when used as a fill. */
  accentWarm: palette.copperRust,
  accentWarmText: palette.carbonBlack,

  /** Cool accent (mineral teal) — the "master" badge and other cool emphasis,
   *  a deliberate counterpoint to the warm ember CTAs. */
  accentCool: palette.mineralTeal,
  accentCoolText: palette.carbonBlack,

  /** Status banner color pairs (background / foreground). */
  errorBg: "#36140f",
  errorFg: "#f0a98f",
  okBg: "#0e2b2c",
  okFg: palette.glacialBlue,
  infoBg: "#152629",
  infoFg: palette.mineralTeal,
} as const;

/**
 * Brand ramp for Fluent. The 16 steps run from a deep carbon-tinted maroon up
 * through ember (~90) and copper (~130) to a pale cream. Fluent derives brand
 * backgrounds, links and focus rings from this; the explicit token overrides
 * below pin the primary-button color to ember regardless of how Fluent indexes
 * the ramp.
 */
const brandRamp: BrandVariants = {
  10: "#0a0502",
  20: "#2a0e03",
  30: "#451705",
  40: "#5d1f06",
  50: "#762807",
  60: "#8f3209",
  70: "#a8421a",
  80: "#c25522",
  90: "#d8692d", // ember
  100: "#e07a3d",
  110: "#e88b4f",
  120: "#f09c61",
  130: "#faaf3b", // copper rust
  140: "#fbc169",
  150: "#fcd596",
  160: "#fde9c8",
};

const base = createDarkTheme(brandRamp);

/**
 * The applied theme: Fluent's dark theme with the neutral surfaces shifted onto
 * the carbon-black family and the brand background pinned to ember + white text.
 */
export const brandTheme: Theme = {
  ...base,

  // Surfaces → carbon family.
  colorNeutralBackground1: ui.surface,
  colorNeutralBackground1Hover: "#18222c",
  colorNeutralBackground1Pressed: "#0b1116",
  colorNeutralBackground1Selected: "#1c2731",
  colorNeutralBackground2: ui.surfaceRaised,
  colorNeutralBackground3: "#1f2a35",
  colorNeutralBackground4: "#0c1217",
  colorNeutralBackgroundDisabled: "#161f27",

  // Text → white / platinum / graphite.
  colorNeutralForeground1: ui.text,
  colorNeutralForeground2: ui.textSecondary,
  colorNeutralForeground3: ui.textMuted,
  colorNeutralForeground4: ui.textMuted,
  colorNeutralForegroundDisabled: "#5a6670",

  // Borders.
  colorNeutralStroke1: ui.border,
  colorNeutralStroke2: ui.borderSubtle,
  colorNeutralStroke3: ui.borderSubtle,

  // Primary accent → ember, with white foreground.
  colorBrandBackground: palette.ember,
  colorBrandBackgroundHover: "#c25522",
  colorBrandBackgroundPressed: "#a8421a",
  colorBrandBackgroundSelected: "#c25522",
  colorNeutralForegroundOnBrand: palette.white,
  colorCompoundBrandBackground: palette.ember,
  colorCompoundBrandBackgroundHover: "#c25522",
  colorCompoundBrandBackgroundPressed: "#a8421a",
  colorCompoundBrandForeground1: palette.copperRust,
  colorCompoundBrandStroke: palette.ember,
};
